import * as React from "react";
import { useCallback, useState } from "react";
import { Modal } from "@fluentui/react";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import HeaderPanel from "../../layout/HeaderPanel";
import FooterPanel from "../../layout/FooterPanel";
import { ButtonVariants } from "../../../styles/fluent/ButtonVariants";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { WithApiFileLocationProvider } from "../WithApiFileLocationProvider";
import { fetchDocumentList } from "../../../api/document/Location";
import { mapDocumentPartsToFileLocations } from "../fileLocationMapper";
import { DocumentLocationExplorer } from "../DocumentLocationExplorer";
import { FlatPathTreeItem } from "../../generic/FlatPathTreeExplorer";
import { DocumentPart } from "../../../api/models";
import {
  CAPTION_COMPONENT_SOURCE_SELECT_SOURCE,
  CAPTION_COMPONENT_SOURCE_SELECT_SOURCE_MESSAGE,
} from "../outlineCaptions";

const CAPTION_SELECT = "cof.base.select";
const CAPTION_CANCEL = "cof.base.cancel";

export interface ComponentSourcePickerProps {
  onCancel: () => void;
  onSelect: (documentPart: DocumentPart) => void;
}

/**
 * First-page-only document picker (folders and documents at the current level), reproducing the
 * "folders" page of ObjectComponentReferenceSelector: no outline, no component list, no search box.
 */
export const ComponentSourcePicker: React.FunctionComponent<ComponentSourcePickerProps> = ({ onCancel, onSelect }) => {
  const [selectedItem, setSelectedItem] = useState<FlatPathTreeItem>();

  const getItems = useCallback(
    async (path?: string) => mapDocumentPartsToFileLocations(await fetchDocumentList(path)),
    []
  );

  const selectDisabled = !selectedItem?.document || selectedItem.document.folder || !selectedItem.document.id;

  return (
    <Modal
      isOpen
      onDismiss={onCancel}
      styles={{ main: { minHeight: "100%", minWidth: "100%" } }}
      data-testid="component-source-picker"
    >
      <HeaderPanel applyPadding>
        <span data-testid="component-source-picker-title">{formatMessage(CAPTION_COMPONENT_SOURCE_SELECT_SOURCE)}</span>
      </HeaderPanel>
      <div
        data-testid="component-source-picker-message"
        style={{
          display: "flex",
          color: "var(--color-grey)",
          fontSize: "14px",
          padding: "5px",
        }}
      >
        {formatMessage(CAPTION_COMPONENT_SOURCE_SELECT_SOURCE_MESSAGE)}
      </div>
      <WithApiFileLocationProvider selectDepth="ITEM" getItems={getItems}>
        <DocumentLocationExplorer
          suppressOpenOnActivate
          onSelectItem={setSelectedItem}
          documentPartWithStatus={null}
          setDocumentPartWithStatus={() => {}}
        />
      </WithApiFileLocationProvider>
      <FooterPanel>
        <PrimaryButton
          data-testid="component-source-picker-cancel"
          id="component_source_picker_cancel"
          className={ButtonVariants.white}
          text={formatMessage(CAPTION_CANCEL)}
          onClick={onCancel}
          style={{ float: "left" }}
        />
        <PrimaryButton
          data-testid="component-source-picker-select"
          id="component_source_picker_select"
          className={ButtonVariants.primary}
          text={formatMessage(CAPTION_SELECT)}
          disabled={selectDisabled}
          onClick={() => onSelect(selectedItem.document)}
          style={{ float: "right" }}
        />
      </FooterPanel>
    </Modal>
  );
};
